import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, palette } from '@munifocus/ui';
import type { Permission } from '@munifocus/access';
import type { OperationsStatus } from '@munifocus/contracts-gov';
import { useAuth } from '../providers/AuthProvider';
import { useOptionalScope } from '../providers/ScopeProvider';

/**
 * Signed in, and — when `needsAny` is given — actually allowed to be here.
 *
 * Hiding a button is not a guard. An unappointed contractor's employee had no
 * "New file" control, but `/files/new` typed into the address bar still handed
 * them the intake form; the API refuses the save, so the only thing they lost
 * was the time spent filling it in. The permission check belongs on the route
 * as well as on the control that leads to it.
 */
export function ProtectedRoute({
  children,
  needsAny,
  orOperations,
}: {
  children: ReactNode;
  /**
   * Any one of these admits the route; absent means signed-in is enough.
   *
   * Any, not all: an officer holds `file:create` and reads assigned files
   * through a per-file role, while a clerk holds municipality-wide `file:read`.
   * Requiring both would lock out one of them.
   */
  needsAny?: Permission[];
  /**
   * Also admits the route when the account-wide operations status says so.
   * A contractor keeps its books at the company while it works in a town's
   * scope, so the active scope's permissions alone would bounce its
   * administrator off the invoices the rail offers.
   */
  orOperations?: (status: OperationsStatus) => boolean;
}) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  // Optional, not required: this wraps every route, including in component
  // tests that render a screen without a ScopeProvider above it. A missing
  // provider is a state to render, not an error to throw — 165 specs said so.
  const scope = useOptionalScope();

  // Wait for the initial /auth/me probe before deciding — otherwise a reload
  // would flash the login page for an officer who is already signed in.
  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: palette.background.default,
        }}
      >
        <CircularProgress size={28} thickness={5} sx={{ color: palette.brand.accent }} />
      </Box>
    );
  }

  // Where they were going rides along, so a link to a file opened from an email
  // lands on that file after sign-in rather than on the dashboard.
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  // Scopes decide the answer, so a redirect before they arrive would bounce a
  // permitted officer off their own page on a cold reload.
  if (needsAny && scope) {
    // Scopes decide the answer, so a redirect before they arrive would bounce a
    // permitted officer off their own page on a cold reload.
    if (scope.isLoading) return null;
    if (!needsAny.some((permission) => scope.can(permission))) {
      if (orOperations) {
        if (scope.operationsLoading) return null;
        if (scope.operations && orOperations(scope.operations)) return <>{children}</>;
      }
      return <Navigate to="/" replace />;
    }
  }
  return <>{children}</>;
}
